import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';

import { ErrorsNavigationContextProvider } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContextProvider';
import { useFlattenDataAndUIConfig } from 'hooks/useFlattenDataAndUIConfig';
import { useIssueResolver } from 'hooks/useIssueResolver';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { WebEDIError } from 'services/webediErrors/types';
import { setIsBigMessage, useWebEDIStore } from 'stores/webedi/webedi';
import { isBigMessage as checkIsBigMessage, isMessageBEValidationComplete } from 'support/helpers/messages/messages';
import { isCurrentPartnerTheMessageReceiver } from 'support/helpers/relationships/relationships';
import { MessageV2DTO, PartnerDTO, ProcessSpecificationDTO, TransactionOriginApp } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';
import { Form } from 'components/Form/Form';
import { Page } from 'components/Page/Page';
import { DocumentSections } from 'components/WebEDI/DocumentSections/DocumentSections';
import { hasSummary } from 'components/WebEDI/DocumentSummary/helpers';
import { MessageFieldErrorModal } from 'components/WebEDI/MessageFieldErrorModal/MessageFieldErrorModal';
import { SidebarDocumentSummary } from 'components/WebEDI/SidebarDocumentSummary/SidebarDocumentSummary';
import { ValidationPendingBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/components';
import { WebEDIMessageErrorBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/WebEDIMessageErrorBanner';
import { WebEDISidebar } from 'components/WebEDI/WebEDISidebar/WebEDISidebar';

import { useMessageViewForm } from '../hooks/useMessageViewForm';
import { ClonedDocumentBanner } from '../../ClonedDocumentBanner/ClonedDocumentBanner';
import { isDocumentClone } from '../../ClonedDocumentBanner/helpers';
import { DroppedMessageAlert } from '../../DroppedMessageAlert/DroppedMessageAlert';
import { DuplicateAndChangeDocumentBanner } from '../../DuplicateAndChangeDocumentBanner/DuplicateAndChangeDocumentBanner';
import { shouldDetermineTheDuplicateAndChangeDocumentState } from '../../DuplicateAndChangeDocumentBanner/helpers';
import { ReviewBanner } from '../components/ReviewBanner/ReviewBanner';
import { MessageHeader } from '../MessageHeader/MessageHeader';

const DISPLAY_MODE = DisplayModes.view;
type MessageViewPageV2UIProps = {
  message: MessageV2DTO | undefined;
  messageOriginApp: TransactionOriginApp;
  relatedTransactions: Array<MessageV2DTO> | undefined;
  currentPartner: PartnerDTO | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
};

export const ExistingMessageUI = ({
  message,
  messageOriginApp,
  relatedTransactions,
  currentPartner,
  processSpecFields,
}: MessageViewPageV2UIProps) => {
  const { data: flattenData, uiConfig } = useFlattenDataAndUIConfig(processSpecFields, message?.canonical);

  useEffect(() => {
    setIsBigMessage(checkIsBigMessage(flattenData ?? undefined));
  }, [flattenData]);

  const issueResolver = useIssueResolver(message?.issue_resolver_id);
  const { formMethods, isLoading, isValidationComplete } = useMessageViewForm({
    flattenData,
    message,
    processSpecFields,
  });

  const isBigMessage = useWebEDIStore((state) => state.isBigMessage);
  const isLoadingDatachecksErrors = !isValidationComplete && Boolean(isBigMessage);
  const isMessageLoading = isBigMessage ? isLoading : isLoading || !isValidationComplete;
  const showValidationPendingBanner =
    isBigMessage && !isMessageBEValidationComplete(message?.internal_state) && !message?.blocking_error;

  return (
    <Page>
      <Form id="webedi-document">
        <FormProvider {...formMethods}>
          <ErrorsNavigationContextProvider<WebEDIError>
            service={WebEDIErrorsServiceSingleton}
            ErrorModalComponent={MessageFieldErrorModal}
          >
            <Page.Head
              rightContent={
                !!message && (
                  <MessageHeader
                    mode={DISPLAY_MODE}
                    message={message}
                    messageOriginApp={messageOriginApp}
                    currentPartner={currentPartner}
                    relatedTransactions={relatedTransactions}
                  />
                )
              }
            />
            <Page.Section
              isLoading={isMessageLoading}
              topBanner={
                <>
                  <DroppedMessageAlert message={message} />
                  {shouldDetermineTheDuplicateAndChangeDocumentState(message) && (
                    <DuplicateAndChangeDocumentBanner message={message} />
                  )}
                  {isDocumentClone(message) && <ClonedDocumentBanner message={message} />}
                  {showValidationPendingBanner && <ValidationPendingBanner />}
                  {message?.blocking_error && flattenData ? (
                    <WebEDIMessageErrorBanner
                      mode={DISPLAY_MODE}
                      blockingError={message.blocking_error}
                      issueResolver={issueResolver}
                      senderPartner={message.sender_partner}
                      receiverPartner={message.receiver_partner}
                      isCurrentPartnerReceiver={isCurrentPartnerTheMessageReceiver(message, currentPartner?.id)}
                      isLoadingDatachecksErrors={isLoadingDatachecksErrors}
                    />
                  ) : null}
                </>
              }
              rightAside={
                <WebEDISidebar
                  document={message}
                  documentOriginApp={messageOriginApp}
                  relatedTransactions={relatedTransactions}
                  currentPartner={currentPartner}
                >
                  {hasSummary(message?.type) ? <SidebarDocumentSummary documentType={message?.type} /> : null}
                </WebEDISidebar>
              }
            >
              {message ? (
                <div className="flex flex-col gap-6">
                  <ReviewBanner message={message} />

                  <DocumentSections
                    mode={DISPLAY_MODE}
                    uiConfig={uiConfig}
                    documentType={message.type}
                    variables={message.receiver_partner ? { receiverPartnerName: message.receiver_partner?.name } : {}}
                    senderPartner={message.sender_partner}
                    receiverPartner={message.receiver_partner}
                  />
                </div>
              ) : null}
            </Page.Section>
          </ErrorsNavigationContextProvider>
        </FormProvider>
      </Form>
    </Page>
  );
};
